import { type ModuleApiInfo } from "@client/content/selectors";
import { type RootContainerProps } from "@client/content/types";
import { unwrapResponsePromise } from "@client/lib/api";
import appInfo from "@common/appInfo";
import { TransferModal } from "@common/components/Bank/TransferModal";
import { type TransferApi } from "@common/components/Bank/types";
import { useI18n } from "@common/hooks/i18n";
import getOptions from "@common/lib/options";
import { getApi as getAccountApi } from "@common/modules/account/api";
import { type Account } from "@common/modules/account/types";
import { getApi as getUserApi } from "@common/modules/user/api";
import * as React from "react";
import api from "../api";
import { type MemberWallet } from "../types";

export type MapProps = RootContainerProps & {
  accounts: MemberWallet.View.Account[];
  apiInfo: ModuleApiInfo;
  defaultAccount: MemberWallet.View.Account;
  error: string | null;
  fieldStyle: React.CSSProperties;
  isVisible: boolean;
  status: MemberWallet.Status;
};

export type ActionProps = {
  onClose: () => void;
  onSubmitted: (data: MemberWallet.Api.TransferResponse) => void;
};

export type Props = ActionProps & MapProps;
export default (props: Props) => {
  const i18n = useI18n();

  const options = React.useMemo(() => getOptions(i18n), [i18n]);

  const transferApi = React.useMemo((): TransferApi => {
    const accountApi = getAccountApi(i18n);
    const userApi = getUserApi(i18n);

    return {
      getBlockchainNetworks: () => options.getOptions("bank", "blockchainNetworks"),
      getCurrencies: () => options.currencies({ isTransfer: true }, "no-cache"),
      getIsWalletTransferEnabled: () => Promise.resolve(appInfo.isWalletToWalletTransfersEnabled),
      lookupAccount: accountApi.lookupAccount,
      otp: {
        getSteps: userApi.otp.accounts.getSteps,
        sendCode: userApi.otp.accounts.send
      },
      recipient: {
        add: (data) => unwrapResponsePromise(api.createRecipient(props.apiInfo, data)),
        get: () => unwrapResponsePromise(api.getRecipients(props.apiInfo)),
        getDynamicData: (data) => unwrapResponsePromise(api.getRecipientDynamicData(props.apiInfo, data)),
        update: (data) => unwrapResponsePromise(api.updateRecipient(props.apiInfo, data))
      }
    };
  }, [props.apiInfo]);

  const submit = React.useCallback(
    async (data: Account.Api.TransferRequest) => {
      const res = await unwrapResponsePromise(api.transfer(props.apiInfo, data.fromAccountId, data));
      if (res.transaction) {
        props.onSubmitted(res);
      }
      return res.transaction;
    },
    [props.onSubmitted]
  );

  return (
    <TransferModal
      api={transferApi}
      defaultAccount={props.defaultAccount}
      recipientPreviewStyle={{
        borderRadius: props.fieldStyle.borderRadius,
        borderWidth: props.fieldStyle.borderWidth,
        borderColor: props.fieldStyle.borderColor,
        borderStyle: "solid"
      }}
      isVisible={props.isVisible}
      onClose={props.onClose}
      submit={submit}
      userAccounts={props.accounts}
    />
  );
};
